import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Select,
  Alert,
  Empty,
  Typography,
  Tag,
} from "antd";
import { SearchOutlined, ClearOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import apiHelpers from "./data";
import productBucketsApi from "./productBucketsData";
import buildColumns from "./columns";
import ProductDetailModal from "./ProductDetailModal";
import ProductBucketDrawer from "./ProductBucketDrawer";
import ProductKeywordDrawer from "./ProductKeywordDrawer";
import { STORES, DEFAULT_STORE } from "./stores";
import { hashForLabel } from "../BlogsTable/stores";
import { triggerNotification } from "../../common/notify";
import { SHARED_STORE_LS_KEY } from "../common/storeDefaults";

const LS_STORE_KEY = SHARED_STORE_LS_KEY;

const STORE_OPTIONS = STORES.map((s) => ({
  value: s.label,
  label: `${s.label} — ${s.name}`,
}));
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const TAG_FILTER_OPTIONS = [
  { value: "all",          label: "Has Tag: All" },
  { value: "with_tags",    label: "Has Tag: Yes" },
  { value: "without_tags", label: "Has Tag: No" },
];

const readInitialFilters = (searchParams) => {
  const validLabels = STORES.map((s) => s.label);
  const lsStore = localStorage.getItem(LS_STORE_KEY);
  const qpStore = searchParams.get("pstore");
  const store_label = validLabels.includes(qpStore)
    ? qpStore
    : validLabels.includes(lsStore)
    ? lsStore
    : DEFAULT_STORE;

  const rawTagFilter = searchParams.get("ptagf");
  return {
    store_label,
    search:     searchParams.get("psearch") || "",
    has_tag:    searchParams.get("phtag") || "",
    bucket_id:  null,
    default_only: false,
    prod_type_id: null,
    is_active:  null,
    has_bucket: null,
    tag_filter: ["with_tags", "without_tags"].includes(rawTagFilter) ? rawTagFilter : "all",
    page:  Math.max(parseInt(searchParams.get("ppage"), 10) || 1, 1),
    limit: PAGE_SIZE_OPTIONS.includes(parseInt(searchParams.get("plimit"), 10))
      ? parseInt(searchParams.get("plimit"), 10)
      : 20,
  };
};

const ProductsTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => readInitialFilters(searchParams));
  const [searchInput, setSearchInput] = useState(filters.search);
  const [hasTagInput, setHasTagInput] = useState(filters.has_tag);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const abortRef = useRef(null);

  const [detailId, setDetailId] = useState(null);
  const [bucketProduct, setBucketProduct] = useState(null);
  const [tagsProduct, setTagsProduct] = useState(null);

  const [buckets, setBuckets] = useState([]);
  const [loadingBuckets, setLoadingBuckets] = useState(false);

  const [productTypes, setProductTypes] = useState([]);
  const [loadingProductTypes, setLoadingProductTypes] = useState(false);

  // variantId → { typeName, bucketKey, bucketName }
  const [assignmentMap, setAssignmentMap] = useState({});

  // variantId → tag count (populated when ProductKeywordDrawer loads/saves)
  const [tagCountMap, setTagCountMap] = useState({});

  const hasFilters = useMemo(
    () =>
      !!filters.bucket_id ||
      !!filters.default_only ||
      filters.prod_type_id !== null ||
      filters.is_active !== null ||
      filters.has_bucket !== null ||
      (filters.tag_filter && filters.tag_filter !== "all") ||
      !!filters.has_tag,
    [filters.bucket_id, filters.default_only, filters.prod_type_id, filters.is_active, filters.has_bucket, filters.tag_filter, filters.has_tag]
  );

  const currentStoreHash = useMemo(
    () => hashForLabel(filters.store_label),
    [filters.store_label]
  );

  const bucketsById = useMemo(() => {
    const map = {};
    buckets.forEach((b) => {
      map[String(b.id)] = b;
    });
    return map;
  }, [buckets]);

  useEffect(() => {
    localStorage.setItem(LS_STORE_KEY, filters.store_label);
  }, [filters.store_label]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("pstore", filters.store_label);
    if (filters.search) next.set("psearch", filters.search);
    else next.delete("psearch");
    if (filters.has_tag) next.set("phtag", filters.has_tag);
    else next.delete("phtag");
    if (filters.tag_filter && filters.tag_filter !== "all") next.set("ptagf", filters.tag_filter);
    else next.delete("ptagf");
    next.delete("pbucket");
    next.delete("pdefault");
    next.set("ppage", String(filters.page));
    next.set("plimit", String(filters.limit));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    if (!currentStoreHash) {
      setBuckets([]);
      return;
    }
    setLoadingBuckets(true);
    (async () => {
      try {
        const list = await productBucketsApi.fetchBucketsForStore(currentStoreHash);
        if (!cancelled) setBuckets(list);
      } catch (err) {
        if (!cancelled) {
          setBuckets([]);
          const mapped = productBucketsApi.mapError(err);
          triggerNotification({
            type: "error",
            title: "Failed to load buckets",
            message: mapped.message,
          });
        }
      } finally {
        if (!cancelled) setLoadingBuckets(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentStoreHash]);

  useEffect(() => {
    if (
      filters.bucket_id &&
      buckets.length > 0 &&
      !buckets.find((b) => String(b.id) === String(filters.bucket_id))
    ) {
      setFilters((prev) => ({ ...prev, bucket_id: null, page: 1 }));
    }
  }, [buckets, filters.bucket_id]);

  useEffect(() => {
    let cancelled = false;
    if (!currentStoreHash) {
      setProductTypes([]);
      return;
    }
    setLoadingProductTypes(true);
    (async () => {
      try {
        const list = await productBucketsApi.fetchProductTypesForStore(currentStoreHash);
        if (!cancelled) setProductTypes(list);
      } catch {
        if (!cancelled) setProductTypes([]);
      } finally {
        if (!cancelled) setLoadingProductTypes(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentStoreHash]);

  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((prev) =>
        prev.search === searchInput ? prev : { ...prev, search: searchInput, page: 1 }
      );
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((prev) =>
        prev.has_tag === hasTagInput ? prev : { ...prev, has_tag: hasTagInput, page: 1 }
      );
    }, 300);
    return () => clearTimeout(id);
  }, [hasTagInput]);

  const fetchData = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setFetching(true);
    setLoadError(null);
    try {
      const [{ items, pagination: pg }, assignmentsResult] = await Promise.all([
        apiHelpers.fetchProducts(filters, { signal: controller.signal }),
        currentStoreHash
          ? productBucketsApi.listAssignments({ store_hash: currentStoreHash, limit: 100 })
          : Promise.resolve({ items: [] }),
      ]);

      if (controller.signal.aborted) return;

      // Build variantId → { typeName, bucketId } map
      const aMap = {};
      (assignmentsResult.items || []).forEach((a) => {
        if (a.variantId) {
          aMap[String(a.variantId)] = {
            typeName: a.productType?.typeName || a.productType?.type_name || null,
            bucketId: a.bucketId ?? null,
          };
        }
      });
      setAssignmentMap(aMap);
      setRows(items);
      setPagination(pg);
    } catch (err) {
      if (controller.signal.aborted) return;
      const mapped = apiHelpers.mapError(err);
      if (mapped.code === "CANCELLED") return;
      setLoadError(mapped);
      setRows([]);
      setPagination({ total: 0, totalPages: 0 });
      triggerNotification({
        type: "error",
        title: "Failed to load products",
        message: mapped.message,
      });
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setLoading(false);
      setFetching(false);
    }
  }, [filters, currentStoreHash]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current.abort();
    },
    []
  );

  const updateFilter = (patch) =>
    setFilters((prev) => ({ ...prev, ...patch, page: patch.page ?? 1 }));

  const handleOpenDetail = useCallback((record) => setDetailId(record.id), []);

  const storeHashResolver = useCallback(
    (record) => hashForLabel(record?.store || filters.store_label),
    [filters.store_label]
  );

  const handleManageBucket = useCallback(
    (record) => {
      const hash = storeHashResolver(record);
      if (!hash) {
        triggerNotification({
          type: "error",
          title: "Store not configured",
          message: `Store "${record.store}" has no BC hash mapped in BlogsTable/stores.js.`,
        });
        return;
      }
      setBucketProduct({
        record,
        storeHash: hash,
        storeLabel: record.store || filters.store_label,
      });
    },
    [storeHashResolver, filters.store_label]
  );

  const handleManageTags = useCallback(
    (record) => {
      const hash = storeHashResolver(record);
      if (!hash) {
        triggerNotification({
          type: "error",
          title: "Store not configured",
          message: `Store "${record.store}" has no BC hash mapped.`,
        });
        return;
      }
      setTagsProduct({
        record,
        storeHash: hash,
        storeLabel: record.store || filters.store_label,
      });
    },
    [storeHashResolver, filters.store_label]
  );

  const handleToggleActive = useCallback(async (record, checked) => {
    const prev = record.active;
    setRows((rows) =>
      rows.map((r) => r.key === record.key ? { ...r, active: checked } : r)
    );
    try {
      const res = await apiHelpers.toggleProductActive(record, checked);
      if (res?.error) throw new Error(res.error);
      triggerNotification({
        type: "success",
        title: checked ? "Product activated" : "Product deactivated",
        message: record.sapName || record.bcSku || `Product #${record.id}`,
      });
    } catch (err) {
      setRows((rows) =>
        rows.map((r) => r.key === record.key ? { ...r, active: prev } : r)
      );
      const mapped = apiHelpers.mapError(err);
      triggerNotification({
        type: "error",
        title: "Toggle failed",
        message: mapped.message || err.message,
      });
    }
  }, []);

  const productTypesById = useMemo(() => {
    const map = {};
    productTypes.forEach((pt) => { map[String(pt.id)] = pt; });
    return map;
  }, [productTypes]);

  const columns = useMemo(() => {
    return buildColumns({
      onOpenDetail:    handleOpenDetail,
      onManageBucket:  handleManageBucket,
      onManageTags:    handleManageTags,
      onToggleActive:  handleToggleActive,
      storeHashResolver,
      bucketsById,
      productTypesById,
      assignmentMap,
      tagCountMap,
    });
  }, [
    handleOpenDetail,
    handleManageBucket,
    handleManageTags,
    handleToggleActive,
    storeHashResolver,
    bucketsById,
    productTypesById,
    assignmentMap,
    tagCountMap,
  ]);

  const showFullPageLoader = loading && !rows.length;

  return (
    <>
      <div style={{ marginBottom: 12 }} className="TableFilterBox">
        {/* Row 1 */}
        <Space wrap style={{ marginBottom: 8 }}>
          <Select
            value={filters.store_label}
            onChange={(v) => updateFilter({ store_label: v })}
            options={STORE_OPTIONS}
            style={{ width: 220 }}
          />
          <Input
            allowClear
            placeholder="Search by name or SKU"
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ width: 280 }}
          />
          <Input
            allowClear
            placeholder="Has tag"
            prefix={<SearchOutlined />}
            value={hasTagInput}
            onChange={(e) => setHasTagInput(e.target.value)}
            style={{ width: 160 }}
            disabled={!currentStoreHash}
          />
          <Select
            value={filters.bucket_id || undefined}
            onChange={(v) => updateFilter({ bucket_id: v ? Number(v) : null })}
            options={[
              { value: undefined, label: "All buckets" },
              ...buckets.map((b) => ({ value: Number(b.id), label: b.displayName })),
            ]}
            placeholder={loadingBuckets ? "Loading buckets…" : "Filter by bucket"}
            loading={loadingBuckets}
            allowClear
            style={{ width: 220 }}
            disabled={!currentStoreHash}
          />
          <Select
            value={filters.prod_type_id || undefined}
            onChange={(v) => updateFilter({ prod_type_id: v ? Number(v) : null })}
            options={productTypes.map((pt) => ({ value: pt.id, label: pt.typeName }))}
            placeholder={loadingProductTypes ? "Loading types…" : "Filter by product type"}
            loading={loadingProductTypes}
            allowClear
            style={{ width: 200 }}
            disabled={!currentStoreHash}
          />
          <Select
            value={filters.is_active === null ? "" : filters.is_active}
            onChange={(v) => updateFilter({ is_active: v === "" ? null : Number(v) })}
            options={[
              { value: "", label: "All products" },
              { value: 1, label: "Active on store" },
              { value: 0, label: "Inactive on store" },
            ]}
            style={{ width: 160 }}
            disabled={!currentStoreHash}
          />
          <Select
            value={filters.has_bucket !== null ? filters.has_bucket : undefined}
            onChange={(v) => updateFilter({ has_bucket: v !== undefined ? Number(v) : null })}
            options={[
              { value: 1, label: "Yes" },
              { value: 0, label: "No" },
            ]}
            placeholder="Bucket assigned"
            allowClear
            style={{ width: 180 }}
            disabled={!currentStoreHash}
          />
        </Space>
        {/* Row 2 */}
        <Space wrap>
          <Select
            value={filters.tag_filter}
            onChange={(v) => updateFilter({ tag_filter: v })}
            options={TAG_FILTER_OPTIONS}
            style={{ width: 160 }}
            disabled={!currentStoreHash}
          />
          <Select
            value={filters.default_only ? 1 : ""}
            onChange={(v) => updateFilter({ default_only: v === 1 })}
            options={[
              { value: "", label: "All products" },
              { value: 1, label: "Default only" },
            ]}
            style={{ width: 160 }}
            disabled={!currentStoreHash}
          />
          <Select
            value={filters.limit}
            onChange={(v) => updateFilter({ limit: v })}
            options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: `${n} / page` }))}
            style={{ width: 120 }}
          />
          {hasFilters && (
            <Button
              icon={<ClearOutlined />}
              onClick={() => {
                setHasTagInput("");
                updateFilter({
                  bucket_id: null,
                  default_only: false,
                  prod_type_id: null,
                  is_active: null,
                  has_bucket: null,
                  tag_filter: "all",
                  has_tag: "",
                });
              }}
            >
              Clear filters
            </Button>
          )}
        </Space>
      </div>

      {hasFilters && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <Space wrap size={6}>
              <span>Filtered:</span>
              {filters.bucket_id && bucketsById[String(filters.bucket_id)] && (
                <>
                  <span>for</span>
                  <Tag color="green">
                    {bucketsById[String(filters.bucket_id)].bucketKey}
                  </Tag>
                  <span>
                    {bucketsById[String(filters.bucket_id)].displayName}
                  </span>
                </>
              )}
              {filters.default_only && <Tag color="gold">Default only</Tag>}
              {filters.prod_type_id && productTypes.find((pt) => pt.id === filters.prod_type_id) && (
                <>
                  <span>type:</span>
                  <Tag color="blue">
                    {productTypes.find((pt) => pt.id === filters.prod_type_id).typeName}
                  </Tag>
                </>
              )}
              {filters.is_active === 0 && <Tag color="red">Inactive on store</Tag>}
              {filters.is_active === null && <Tag color="default">All (active + inactive)</Tag>}
              {filters.has_bucket === 1 && <Tag color="cyan">Has bucket</Tag>}
              {filters.has_bucket === 0 && <Tag color="orange">No bucket assigned</Tag>}
              {filters.tag_filter === "with_tags" && <Tag color="geekblue">Tagged products</Tag>}
              {filters.tag_filter === "without_tags" && <Tag color="volcano">Untagged products</Tag>}
              <span style={{ color: "rgba(0,0,0,0.45)" }}>
                · clear filters to return to the full product list
              </span>
            </Space>
          }
        />
      )}

      {loadError && !fetching && (
        <Alert
          type="error"
          showIcon
          message={loadError.message}
          action={
            <Button size="small" onClick={fetchData}>
              Retry
            </Button>
          }
          style={{ marginBottom: 12 }}
        />
      )}

      <div
        className="TableBox"
        style={{
          maxHeight: 700,
          overflow: "auto",
          opacity: fetching && !showFullPageLoader ? 0.6 : 1,
          transition: "opacity 0.15s",
        }}
      >
        <Table
          rowKey="key"
          columns={columns}
          dataSource={rows}
          loading={showFullPageLoader}
          locale={{
            emptyText: (
              <Empty
                description={
                  hasFilters ? (
                    <span>
                      No products match the current filters in{" "}
                      <b>{filters.store_label}</b>.
                    </span>
                  ) : (
                    <span>
                      No products found for <b>{filters.store_label}</b>. Try changing the
                      store or search term.
                    </span>
                  )
                }
              />
            ),
          }}
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            total: pagination.total,
            showSizeChanger: false,
            showQuickJumper: true,
            position: ["bottomLeft"],
            showTotal: (total) => (
              <Typography.Text type="secondary">
                {total} result{total === 1 ? "" : "s"} · Page {filters.page} of{" "}
                {pagination.totalPages || 1}
              </Typography.Text>
            ),
            onChange: (page) => setFilters((prev) => ({ ...prev, page })),
          }}
          bordered
          scroll={{ x: 2030, y: 500 }}
        />
      </div>

      <ProductDetailModal
        open={!!detailId}
        productId={detailId}
        onClose={() => setDetailId(null)}
      />

      <ProductBucketDrawer
        open={!!bucketProduct}
        product={bucketProduct?.record}
        storeHash={bucketProduct?.storeHash}
        storeLabel={bucketProduct?.storeLabel}
        onClose={() => setBucketProduct(null)}
        onSaved={() => {
          if (hasFilters) fetchData();
        }}
      />

      <ProductKeywordDrawer
        open={!!tagsProduct}
        product={tagsProduct?.record}
        storeHash={tagsProduct?.storeHash}
        storeLabel={tagsProduct?.storeLabel}
        onClose={() => setTagsProduct(null)}
        onCountChange={(variantId, count) =>
          setTagCountMap((prev) =>
            prev[variantId] === count ? prev : { ...prev, [variantId]: count }
          )
        }
      />
    </>
  );
};

export default ProductsTable;
